import React, { createContext, useContext } from 'react';

type FeatureFlagMap = Record<string, string | null>;

type FeatureFlagContextValue = {
  flags: FeatureFlagMap;
  loaded: boolean;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: {},
  loaded: false,
});

// Single definition of "this flag is on", so non-hook callers that resolve a
// flag by name (e.g. the asset-class nav config) can't drift from the hooks.
export const isFlagEnabled = (activeVersion: string | null | undefined): boolean =>
  !!activeVersion && activeVersion !== 'disabled';

export const useFeatureFlag = (flagName: string, expectedVersion?: string): boolean => {
  const { flags } = useContext(FeatureFlagContext);
  const activeVersion = flags[flagName];

  if (!isFlagEnabled(activeVersion)) return false;

  return expectedVersion ? activeVersion === expectedVersion : true;
};

export const useFeatureFlagVersion = (flagName: string): string | null => {
  const { flags } = useContext(FeatureFlagContext);
  const version = flags[flagName];

  return version && version !== 'disabled' ? version : null;
};

// True once the flag query has settled (success or error). Route-level redirects
// behind useFeatureFlag must gate on this — until it flips true, flags read as
// disabled and a bookmarked URL would be rewritten before the query resolves.
export const useFeatureFlagsLoaded = (): boolean => {
  return useContext(FeatureFlagContext).loaded;
};

export const FeatureFlagProvider: React.FC<{
  flags: FeatureFlagMap;
  loaded: boolean;
  children: React.ReactNode;
}> = ({ flags, loaded, children }) => (
  <FeatureFlagContext.Provider value={{ flags, loaded }}>{children}</FeatureFlagContext.Provider>
);

export const useAllFeatureFlags = (): FeatureFlagMap => {
  return useContext(FeatureFlagContext).flags;
};
